import type { PolicySection, PolicyTable } from './types'
import { BUSINESS, CONTACT_EMAIL, PRIVACY_OFFICER_EMAIL } from '../content'

/**
 * 전 서비스 방침이 글자 그대로 공유하는 조항.
 *
 * **여기 있는 것은 서비스마다 다를 이유가 없는 것들이다** — 파기 절차, 안전성 확보 조치,
 * 보호책임자, 권익침해 구제, 변경 고지. 문서마다 복사해 두면 한쪽만 고쳐져 서로 어긋나고,
 * 어긋난 방침은 그 자체가 위반이다 (operator.ts를 한 곳에 둔 것과 같은 이유).
 *
 * 서비스마다 다른 것(처리 목적·항목·보유기간·위탁·국외이전)은 각 문서가 직접 쓴다.
 *
 * **문서 언어는 영어다 (2026-08-19 사용자 지시).** 근거 법령은 한국법이라 조문 번호를 그대로
 * 인용하고, 국내 기관·제도의 고유명사는 영문 뒤에 한국어 원문을 괄호로 붙인다 — 이용자가
 * 신고·상담을 하려면 결국 그 한국어 이름으로 기관을 찾아야 한다.
 *
 * 근거: 개인정보 보호법 제30조 필수 기재 11항목,
 *       2025-04 개인정보 처리방침 작성지침, 2023-09 시행 전송요구권·자동화 결정 조항.
 */

/** 개인정보 보호책임자. 1인 사업자라 대표가 겸한다 — 없는 부서를 지어내지 않는다 */
export const PRIVACY_OFFICER = {
  name: `${BUSINESS.representativeRoman} (${BUSINESS.representative})`,
  title: 'Owner — also serving as Chief Privacy Officer and as the contact for access requests',
  /** 책임자 직통. 이용자의 권리 행사 접수는 CONTACT_EMAIL 이다 — content.ts 주석 참고 */
  email: PRIVACY_OFFICER_EMAIL,
} as const

/** 서면 접수처. 국내 우편이 닿는 한국어 원문을 로마자 뒤에 그대로 붙인다 */
const POSTAL_ADDRESS = `${BUSINESS.addressRoman} (${BUSINESS.address})`

export const DISPOSAL: PolicySection = {
  heading: 'Destruction of personal data: procedure and method',
  paragraphs: [
    'When personal data is no longer needed — the retention period has passed, or the purpose of processing has been achieved — the Operator destroys it without delay. Where other legislation requires data to be kept, it is moved to separate storage with restricted access for that period and destroyed together once the period ends.',
  ],
  bullets: [
    'Procedure — data that has become due for destruction is identified and destroyed after review by the Chief Privacy Officer',
    'Electronic files — permanently erased by a method that leaves them unrecoverable. Once removed from the database, they also disappear from backups as the backup cycle passes',
    'Paper documents — shredded or incinerated',
  ],
}

/**
 * 정보주체 권리. 전송요구권(제35조의2)까지 포함한다 — 2025-03-13 시행분이고
 * "전송 방법과 내역 확인 방법"까지 적어야 한다.
 */
export function rights(exerciseRoutes: readonly string[], portabilityItems: string): PolicySection {
  return {
    heading: 'Rights of the data subject and legal representative, and how to exercise them',
    paragraphs: [
      'You may at any time request access to, correction of, deletion of, suspension of the processing of, or transfer of your personal data. Under Article 41(1) of the Enforcement Decree of the Personal Information Protection Act, such a request may be made by electronic mail among other routes, and the Operator acts on it without delay.',
      'Rights over the personal data of a child under the age of 14 may be exercised by their legal representative.',
      'You may exercise your rights through an agent, who must submit a letter of authority. The Operator verifies that a person requesting access, correction, deletion, or suspension of processing is the data subject or a duly authorised agent.',
    ],
    bullets: [
      ...exerciseRoutes,
      `Email — ${CONTACT_EMAIL}`,
      // 시행령 제41조 제1항이 서면 접수를 전제하므로 우편 경로를 함께 밝힌다
      `Post — ${POSTAL_ADDRESS}, for the attention of the Chief Privacy Officer of ${BUSINESS.nameRoman} (${BUSINESS.name})`,
    ],
    note: `Right to data portability: you may request that your personal data be transferred to you or to another controller. The items that can be transferred are ${portabilityItems}, provided in JSON or CSV format. Requests sent to the email address above are acknowledged and their outcome reported to that same address, so that reply is where you can check the status and the record of the transfer.`,
  }
}

/**
 * 안전성 확보 조치.
 *
 * **실제로 하고 있는 것만 적는다.** 표준 문안에는 "침입차단·침입탐지 시스템 운영",
 * "전산실·자료보관실 접근 통제" 같은 문장이 들어 있는데, 클라우드 VM 한 대로 운영하는
 * 규모에서 그대로 베끼면 사실과 다른 고지가 된다. 방침의 거짓 기재는 미기재보다 나쁘다.
 */
export const SECURITY: PolicySection = {
  heading: 'Measures to secure personal data',
  paragraphs: ['The Operator takes the following measures to keep personal data secure.'],
  bullets: [
    'Minimum number of people with access — exactly one person, the Operator, can handle personal data',
    'Encryption in transit — HTTPS (TLS) is applied to all communication between your device and the server, and between the server and processors',
    'Access control — the database exposes no port to the outside and is reachable only from the internal service network. Server login accepts public-key authentication only',
    'Separated authentication — services with a login use a dedicated authentication server (Keycloak); the service server only verifies issued tokens. No service stores the password of your social account',
    'Retention and review of access logs — server access and error records are collected, retained, and reviewed for anomalies',
    'Regular backups — the database is backed up on a schedule to guard against loss',
  ],
  note: 'The Operator runs no server room of its own and uses a cloud provider data centre. Physical access control follows the measures of that provider.',
}

export const OFFICER: PolicySection = {
  heading: 'Chief Privacy Officer and contact for access requests',
  paragraphs: [
    'The Operator takes overall responsibility for the handling of personal data and has designated the Chief Privacy Officer below to deal with complaints and remedies relating to the processing of personal data. As a sole proprietorship, the same person also receives and handles access requests.',
  ],
  bullets: [
    `Name — ${PRIVACY_OFFICER.name}`,
    `Position — ${PRIVACY_OFFICER.title}`,
    `Email — ${PRIVACY_OFFICER.email}`,
    `Address — ${POSTAL_ADDRESS}`,
  ],
  note: `You may raise any privacy question, complaint, or request for remedy arising from your use of the service at the contact above, and the Operator will answer and act without delay. Requests to access, correct, delete, suspend the processing of, or transfer your data go to the intake address, ${CONTACT_EMAIL}.`,
}

export const REMEDIES: PolicySection = {
  heading: 'Remedies for infringement of your rights',
  paragraphs: [
    'To seek redress for an infringement of your personal data rights, you may apply to the bodies below for dispute resolution or advice. These bodies are independent of the Operator; approach them if you are not satisfied with the way the Operator has handled your case or need fuller assistance.',
  ],
  bullets: [
    'Personal Information Dispute Mediation Committee (개인정보분쟁조정위원회) — 1833-6972 (www.kopico.go.kr)',
    'Privacy Infringement Report Centre (개인정보침해신고센터) — 118, no area code (privacy.kisa.or.kr)',
    'Supreme Prosecutors’ Office (대검찰청) — 1301, no area code (www.spo.go.kr)',
    'Korean National Police Agency (경찰청) — 182, no area code (ecrm.police.go.kr)',
  ],
  note: 'A person whose rights or interests are infringed by a disposition or omission of a public authority in response to a request under Article 35 (access), Article 36 (correction and deletion), or Article 37 (suspension of processing) of the Personal Information Protection Act may file an administrative appeal as provided by the Administrative Appeals Act.',
}

/** 변경 고지 + 개정 이력. 이력은 문서마다 다르므로 인자로 받는다 */
export function changes(effectiveDate: string, history: readonly string[]): PolicySection {
  return {
    heading: 'Changes to this privacy policy',
    paragraphs: [
      `This policy takes effect on ${effectiveDate}. Where content is added, removed, or amended because of a change in law or in the service, notice is given on this page from seven days before the change takes effect. A change with a material effect on your rights is announced 30 days beforehand.`,
    ],
    bullets: history,
  }
}

/**
 * 자동 수집 장치(쿠키·광고식별자) 조항.
 *
 * 지금 이 함수를 쓰는 서비스는 쿠키도 광고식별자도 쓰지 않는다 — 그래서 "쓰지 않는다"를
 * 명시하는 형태다. 광고(AdMob)를 붙이는 서비스는 이 함수 대신 수집 주체·항목·보유기간을
 * 밝히는 자체 조항을 쓴다.
 */
export function noAutomaticCollection(extra?: string): PolicySection {
  return {
    heading: 'Installation and operation of automatic collection devices, and how to refuse them',
    paragraphs: [
      'The Operator uses no device that automatically collects information about your behaviour — no cookies, no advertising identifiers (ADID/IDFA), no web beacons. Behavioural data is not collected, used, or provided for targeted advertising, and your information is not used for ad targeting or profiling.',
      ...(extra === undefined ? [] : [extra]),
    ],
    note: 'Should advertising be introduced later, the party collecting the data, the purpose, the items, the retention period, and how to refuse will be set out in this policy and announced before it takes effect.',
  }
}

/** 만 14세 미만 아동 조항. 전 서비스가 아동을 대상으로 하지 않는다 */
export const CHILDREN: PolicySection = {
  heading: 'Processing the personal data of children under 14',
  paragraphs: [
    'The service is not directed at children under the age of 14 and does not knowingly collect personal data from them. If the Operator learns that personal data of a child under 14 has been collected without the consent of their legal representative, it is destroyed without delay. A legal representative may request access to, correction of, deletion of, or suspension of the processing of a child’s personal data at the officer contact below.',
  ],
}

/** 국외 이전 표의 열 — 개인정보 보호법 제28조의8이 요구하는 고지 항목 순서다 */
export const OVERSEAS_HEADERS = [
  'Recipient',
  'Country',
  'Data transferred',
  'Time and method of transfer',
  'Purpose of use',
  'Retention and use period',
] as const

/** 위탁 표의 열 */
export const PROCESSOR_HEADERS = [
  'Processor',
  'Entrusted work',
  'Data entrusted',
  'Retention and use period',
] as const

/** 보유 기간 표의 열 */
export const RETENTION_HEADERS = ['Data', 'Retention period', 'Basis'] as const

/**
 * 국외 이전 거부 안내. 수탁자가 전부 국외에 있어 "거부하면 서비스를 못 쓴다"가 사실이라
 * 그렇게 적는다 — 거부 방법이 없다고 적는 것은 위반이고, 있다고 거짓으로 적어도 위반이다.
 */
export const OVERSEAS_REFUSAL =
  'You may refuse the transfer of your personal data abroad. The transfers above are, however, essential to providing the service, so refusing means the feature concerned cannot be used. You can refuse by writing to the email address above, and the Operator will then stop the processing concerned and destroy the data it holds.'

/**
 * Google AdMob 위탁·국외이전 행.
 *
 * **앱마다 다른 것은 "무슨 광고를 띄우는가" 하나뿐이다** — 처리 항목·전송 시점·보유 기간은
 * 같은 SDK 를 쓰니 다를 이유가 없다. 그래서 그 하나만 인자로 받고 나머지는 여기 고정한다.
 * 문서마다 네 줄씩 복사해 두면 한쪽만 고쳐져 서로 어긋난다 (docs/privacy-policy.md §3).
 */
export function admobRow(adPurpose: string): readonly string[] {
  return [
    'Google LLC (AdMob)',
    adPurpose,
    'Device information, advertising identifier (ADID/IDFA), coarse IP-based location',
    'Limited to the moment an ad is served (subject to Google’s retention policy)',
  ]
}

export function admobOverseasRow(adPurpose: string): readonly string[] {
  return [
    'Google LLC (AdMob)',
    'The United States and other countries where Google operates',
    'Device information, advertising identifier, coarse IP-based location',
    'Sent over HTTPS at the moment an ad is displayed',
    adPurpose,
    'Limited to the moment an ad is served (subject to Google’s retention policy)',
  ]
}

/** Firebase Crashlytics 위탁 — 전 서비스가 공통으로 쓴다 */
export const CRASHLYTICS_ROW = [
  'Google LLC (Firebase Crashlytics)',
  'Collection and storage of app error and crash diagnostics, and stability analysis',
  'Diagnostics (installation identifier, app version and build, device model, operating system and device state, screen and action names, error messages and stack traces)',
  '90 days from collection',
] as const

export const CRASHLYTICS_OVERSEAS_ROW = [
  'Google LLC',
  'The United States and other countries where Google operates',
  'Diagnostics (installation identifier, app version and build, device model, operating system and device state, error messages and stack traces)',
  'Sent over HTTPS at the moment an error occurs in the app',
  'App error analysis and improving the stability of the service',
  '90 days from collection',
] as const

/**
 * Cloudflare R2 위탁·국외이전 (2026-08-26 사용자 지시 — "cloudflare r2 로 모든 이미지업로드
 * 기능을 이관").
 *
 * **이 조항이 필요한 이유**는 사진이 놓이는 자리가 바뀌었기 때문이다. 그전까지 프로필 사진과
 * 문의 사진은 운영자의 서버 디스크(도커 볼륨)에만 있었고 — 그래서 수탁자가 없었다 — 지금은
 * Cloudflare 의 오브젝트 스토리지에 있다. 처리 위치가 바뀌면 위탁 고지(제26조)와 국외 이전
 * 고지(제28조의8)가 **둘 다** 필요하다 (docs/privacy-policy.md · docs/object-storage.md).
 *
 * **사진을 안 올리면 나가는 것이 없고, 나가는 것은 이미지 파일뿐이다.** 문의 본문·회신 주소·
 * 계정 정보는 우리 서버와 DB 에 남는다. 그 사실이 문장에 있어야 하고, 코드가 그와 어긋나면
 * 안 된다.
 *
 * 항목과 보유 기간이 서비스마다 다른 것은 **무엇을 두느냐**가 달라서다: 계정이 있는 서비스는
 * 프로필 사진까지, 나머지는 문의 사진뿐이다. 그 둘만 인자로 받고 나머지는 여기 고정한다 —
 * 문서마다 네 줄씩 복사해 두면 한쪽만 고쳐져 서로 어긋난다.
 */
export const R2_ITEMS_SUPPORT =
  'The photographs you attach to a support message (up to three, chosen by you) — the text of the message, your email address, and the app and device details are not passed to this processor'

export const R2_RETENTION_SUPPORT = 'Destroyed together with the message one year after receipt'

export const R2_ITEMS_AVATAR = 'Your profile photo — no other account data is passed to this processor'

export const R2_RETENTION_AVATAR = 'Until you change it, remove it, or delete your account'

export const R2_ITEMS_BOTH =
  'Your profile photo, and the photographs you attach to a support message (up to three, chosen by you) — no other account data, and none of the text of a support message, is passed to this processor'

export const R2_RETENTION_BOTH =
  'The profile photo until you change it, remove it, or delete your account; a support photograph until it is destroyed with the message one year after receipt'

export function cloudflareRow(items: string, retention: string): readonly string[] {
  return [
    'Cloudflare, Inc. (R2 object storage)',
    'Storing the image files the service keeps and returning them to the Operator’s server when they are displayed',
    items,
    retention,
  ]
}

export function cloudflareOverseasRow(items: string, retention: string): readonly string[] {
  return [
    'Cloudflare, Inc.',
    'The United States and other countries where Cloudflare operates',
    items,
    'Sent over HTTPS at the moment you upload the image',
    'Storing the image file so that it can be shown again later',
    retention,
  ]
}

/**
 * "지원 문의" (2026-08-12 도입 — docs/support.md).
 *
 * **2026-08-26 개정: 화면이 앱 밖으로 나갔다** (사용자 지시 — "문의하기를 웹사이트로
 * 배포하고. cottonray.com 에 하나로"). 앱은 이제 기기 브라우저로 `cottonray.com/support`
 * 를 열고, 앱 버전·기기 모델·OS·언어를 **그 주소에 실어** 넘긴다. 받는 항목과 보관
 * 기간은 그대로다 — 옮긴 것은 화면이지 수집이 아니다. 그래서 문장에서 고칠 곳은
 * "앱 안 화면"이라고 적힌 자리뿐이고, 그 자리를 안 고치면 곧 거짓 고지가 된다.
 *
 * **2026-08-24 개정: 사진과 회신 주소를 받는다** (사용자 지시 — "사진첨부기능 더해줘",
 * "지원받을 메일주소(선택사항)도 넣게 해줘", "답변이 no-reply@cottonray.com 보낸사람으로
 * 메일을 실제로 보낼수 있게"). 그전에는 "내용 + 기기 환경"뿐이었다.
 *
 * **둘 다 선택이고, 그 사실이 문장에 있어야 한다** — 안 적어도 문의는 접수되고, 사진을
 * 안 붙여도 마찬가지다. 이용자가 직접 고른 것만 전달되며 앱이 앨범을 훑지 않는다.
 *
 * **주소를 받는 자리는 넷이고 함께 움직인다**: 계약·DB 컬럼·앱 화면·이 조항.
 * 하나만 되돌리면 여기가 곧 거짓 고지가 된다 (docs/support.md §회신).
 *
 * **전 앱이 같은 화면·같은 서버(`admin-support`)를 쓰므로 조항도 한 벌이다.** 문서마다
 * 따로 쓰면 보관 기간이 서로 달라지고, 그러면 같은 사업자가 같은 사실을 다르게 고지한
 * 상태가 된다 (이 파일이 존재하는 이유 그대로).
 *
 * **보관 기간 1년은 서버 설정과 짝이다** — `deploy/support/.env` 의
 * `SUPPORT_RETENTION_DAYS` 와 서버의 파기 잡(`InquiryPurge`)이 그 값을 실제로 집행한다.
 * 한쪽만 바꾸면 이 문장이 거짓 고지가 된다.
 */
export const SUPPORT_PURPOSE =
  'Handling support enquiries — reading what you send from the support page at cottonray.com/support, which the app opens in a screen inside the app, in order to fix errors and improve the service, and replying by email where you have given an address'

export const SUPPORT_ITEM_INPUT =
  '[Provided by you] The text of your support message; the photographs you attach to it, if any (up to three, chosen by you from your device); and your email address, if you choose to give one so that a reply can be sent'

export const SUPPORT_ITEM_AUTO =
  '[Collected automatically] App version, device model, operating system version, and language setting at the time of the message — the app passes these to the support page when it opens it, and they are sent only when you press send'

/** 보유 기간 표의 한 행 */
export const SUPPORT_RETENTION_ROW = [
  'Support message, any photographs and email address you provide with it, and the app/device details attached to it',
  '1 year from receipt',
  'Your consent (given when you send the message)',
] as const

/**
 * 문의 조항의 마무리 문장.
 *
 * **답장이 어디서 어떻게 오는지를 여기서 밝힌다** (2026-08-24 개정). 주소는 선택이고,
 * 적었을 때만 답장에 쓰인다 — 광고·안내에 쓰지 않는다는 약속이 곧 이 문장이다.
 * 보내는 주소(`no-reply@cottonray.com`)가 **수신함이 없다는 것**도 함께 적는다.
 */
export const SUPPORT_NOTE =
  'A support message is sent only when you press send on the support page yourself, and any photograph is one you picked yourself — neither the app nor the page reads your photo library. Giving an email address is optional: without one the message is still received and read, and with one the Operator may write back once from no-reply@cottonray.com, an address that receives no mail. An address given here is used for that reply alone — never for marketing, never to identify an account, and never combined with other data. Messages, photographs, and addresses are destroyed automatically by the server one year after receipt.'

/**
 * 후원(인앱 구입) — 2026-08-22 RevenueCat 도입 (docs/sponsor.md·docs/revenuecat.md).
 *
 * **결제수단 정보는 우리를 거치지 않는다.** 카드번호·계좌는 App Store 와 Google Play 가
 * 처리하고 운영자는 볼 수 없다. 우리 쪽에 남는 것은 "그 구입이 실제로 일어났는가"의
 * 확인 기록뿐이고, 그 확인(영수증 검증)을 RevenueCat 이 대신한다.
 *
 * **계정을 만들지 않는다.** 코드가 부르는 것은 `Purchases.configure({ apiKey })` 하나뿐이라
 * (`@cottonray/purchases-native`) 이용자 식별자는 SDK 가 만든 익명 값이고, 이름·이메일·
 * 광고 식별자를 넘기지 않는다. **로그인이나 사용자 속성을 붙이는 날 이 조항이 거짓이 된다.**
 *
 * **전 앱이 같은 화면(`@cottonray/sponsor-native`)·같은 결제 경로를 쓰므로 조항도 한 벌이다.**
 */
export const SPONSOR_PURPOSE =
  'Handling sponsorships — confirming that a “buy the developer a coffee” purchase actually took place, so that a purchase can be restored and a duplicate charge or refund can be looked into'

export const SPONSOR_ITEM_AUTO =
  '[Collected automatically, and only if you make a sponsorship purchase] An anonymous customer identifier the payment SDK generates for this installation, which product was bought, the store transaction identifier and purchase date, the amount and currency, and the store country, app version, platform, and operating system version'

/** 보유 기간 표의 한 행 */
export const SPONSOR_RETENTION_ROW = [
  'Sponsorship purchase records',
  'Kept while the app is served; destroyed when the Operator closes the app’s payment project, or on your request',
  'Performance of the purchase contract (Article 15(1)4 of the Personal Information Protection Act)',
] as const

/** RevenueCat 위탁 — 후원을 붙인 앱이 공통으로 쓴다 */
export const REVENUECAT_ROW = [
  'RevenueCat, Inc.',
  'Verifying App Store and Google Play receipts for in-app purchases, and keeping the resulting purchase records',
  'Anonymous customer identifier, product purchased, store transaction identifier and purchase date, amount and currency, store country, app version, platform, and operating system version',
  'Until the payment project is closed or deletion is requested',
] as const

export const REVENUECAT_OVERSEAS_ROW = [
  'RevenueCat, Inc.',
  'The United States',
  'Anonymous customer identifier, product purchased, store transaction identifier and purchase date, amount and currency, store country, app version, platform, and operating system version',
  'Sent over HTTPS at the moment you complete a sponsorship purchase',
  'Verifying the store receipt and keeping the purchase record',
  'Until the payment project is closed or deletion is requested',
] as const

/**
 * RevenueCat 위탁 — **REST 경로**(게임 트랙, `game/shared/addons/cottonray/revenuecat.gd`).
 *
 * 같은 수탁자지만 위의 `REVENUECAT_ROW` 와 항목이 다르다. 후원 앱은 공식 SDK 가 앱 버전·
 * 플랫폼·OS 버전까지 실어 보내지만, 게임에는 Godot 용 SDK 가 없어 `POST /v1/receipts` 를
 * 직접 부르므로 **나가는 값이 영수증 페이로드 넷과 스토어 구분 헤더뿐이다.**
 * 항목이 다르면 행도 다르다 — 여기서 SDK 판을 재사용하면 없는 항목을 고지하게 된다.
 */
export const REVENUECAT_RECEIPT_ITEMS =
  'Anonymous customer identifier derived from the device identifier, the store purchase token for that transaction, the product identifier, whether the purchase was a restore, and which store platform it came from'

export const REVENUECAT_RECEIPT_ROW = [
  'RevenueCat, Inc.',
  'Verifying the store receipt for an in-app purchase and keeping the resulting purchase record',
  REVENUECAT_RECEIPT_ITEMS,
  'Until the payment project is closed or deletion is requested',
] as const

export const REVENUECAT_RECEIPT_OVERSEAS_ROW = [
  'RevenueCat, Inc.',
  'The United States',
  REVENUECAT_RECEIPT_ITEMS,
  'Sent over HTTPS at the moment a purchase or a restore completes',
  'Verifying the store receipt and keeping the purchase record',
  'Until the payment project is closed or deletion is requested',
] as const

/**
 * 후원 조항의 마무리 문장.
 *
 * **후원은 아무것도 열어 주지 않는다** (docs/sponsor.md — 소모성 상품이고 기능 잠금이 없다).
 * 그 사실을 여기서 밝히는 이유는, 결제 기록을 남기는 근거가 "기능 제공"이 아니라
 * "구입 확인"뿐임을 분명히 하기 위해서다.
 */
export const SPONSOR_NOTE =
  'Sponsorship is entirely optional and unlocks nothing — the app behaves the same whether or not you sponsor. The payment itself is carried out by the Apple App Store or Google Play, and the Operator never receives your card number, bank details, name, or email address. No advertising identifier is collected, and a sponsorship record is not combined with any other data.'

export function table(
  headers: readonly string[],
  rows: readonly (readonly string[])[],
): PolicyTable {
  return { headers, rows }
}
